import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";

import { softwareDevelopmentData } from "../../../../data/SoftwareDevelopmentData";

// ============================================================
// ANIMATIONS
// ============================================================

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20,
  },

  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

// ============================================================
// FAQ ITEM
// ============================================================

const FAQItem = ({ faq, index, isOpen, onToggle }) => {
  return (
    <motion.div
      variants={itemVariants}
      className="
        group
        w-full
        min-w-0
        overflow-hidden
        rounded-xl
        border
        border-[var(--border-primary)]
        bg-[var(--card-bg-secondary)]
        transition-all
        duration-300
        hover:border-orange-500/30
        sm:rounded-2xl
      "
    >
      {/* =====================================================
          QUESTION
      ====================================================== */}

      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="
          flex
          w-full
          min-w-0
          items-center
          justify-between
          gap-2
          px-3.5
          py-3.5
          text-left
          transition-all
          duration-300
          hover:bg-black/[0.025]

          min-[360px]:gap-3
          min-[360px]:px-4
          min-[360px]:py-4

          sm:gap-4
          sm:px-5
          sm:py-5

          md:px-6
        "
      >
        {/* =================================================
            QUESTION CONTENT
        ================================================== */}

        <div
          className="
            flex
            min-w-0
            flex-1
            items-center
            gap-2.5

            min-[360px]:gap-3

            sm:gap-4
          "
        >
          {/* =================================================
              NUMBER
          ================================================== */}

          <span
            className="
              shrink-0
              text-[9px]
              font-semibold
              tracking-[1.2px]
              text-[var(--brand-primary)]/70

              min-[360px]:text-[10px]

              sm:text-xs
              sm:tracking-[1.5px]
            "
          >
            {String(index + 1).padStart(2, "0")}
          </span>

          {/* =================================================
              QUESTION
          ================================================== */}

          <h3
            className="
              min-w-0
              break-words
              text-xs
              font-semibold
              leading-[1.45]
              text-[var(--text-primary)]
              transition-colors
              duration-300
              group-hover:text-[var(--brand-light)]

              min-[360px]:text-sm
              min-[360px]:leading-5

              sm:text-base
              sm:leading-6

              md:text-lg
            "
          >
            {faq.question}
          </h3>
        </div>

        {/* =================================================
            PLUS / MINUS
        ================================================== */}

        <div
          className={`
            flex
            h-7
            w-7
            shrink-0
            items-center
            justify-center
            rounded-full
            border
            transition-all
            duration-300

            min-[360px]:h-8
            min-[360px]:w-8

            sm:h-9
            sm:w-9

            ${
              isOpen
                ? "border-[var(--brand-primary)] bg-[var(--brand-primary)] text-white"
                : "border-orange-500/20 bg-orange-500/[0.08] text-[var(--brand-primary)]"
            }
          `}
        >
          {isOpen ? (
            <Minus
              size={15}
              className="sm:h-4 sm:w-4"
            />
          ) : (
            <Plus
              size={15}
              className="sm:h-4 sm:w-4"
            />
          )}
        </div>
      </button>

      {/* =====================================================
          ANSWER
      ====================================================== */}

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{
              height: 0,
              opacity: 0,
            }}
            animate={{
              height: "auto",
              opacity: 1,
            }}
            exit={{
              height: 0,
              opacity: 0,
            }}
            transition={{
              duration: 0.3,
              ease: "easeInOut",
            }}
          >
            <div
              className="
                border-t
                border-[var(--border-secondary)]
                px-3.5
                pb-3.5
                pt-3

                min-[360px]:px-4
                min-[360px]:pb-4

                sm:px-5
                sm:pb-5
                sm:pt-4

                md:px-6
              "
            >
              <p
                className="
                  pl-[22px]
                  break-words
                  text-[11px]
                  leading-5
                  text-[var(--text-muted)]
                  transition-colors
                  duration-300
                  group-hover:text-[var(--text-secondary)]

                  min-[360px]:pl-7
                  min-[360px]:text-xs
                  min-[360px]:leading-6

                  sm:pl-9
                  sm:text-sm
                  sm:leading-6
                "
              >
                {faq.answer}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ============================================================
// FAQ SECTION
// ============================================================

const FAQSection = () => {
  const { faqs } = softwareDevelopmentData;

  const [activeIndex, setActiveIndex] = useState(0);

  const handleToggle = (index) => {
    setActiveIndex((current) => (current === index ? -1 : index));
  };

  return (
    <section
      className="
        relative
        w-full
        overflow-hidden
        bg-[var(--bg-primary)]
        py-6

        min-[360px]:py-7

        sm:py-8

        md:py-10

        lg:py-14
      "
    >
      {/* =====================================================
          BACKGROUND GLOW
      ====================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          -left-40
          top-20
          h-56
          w-56
          rounded-full
          bg-orange-500/[0.04]
          blur-[100px]

          min-[360px]:h-64
          min-[360px]:w-64

          sm:h-80
          sm:w-80
          sm:blur-[110px]
        "
      />

      <div
        className="
          pointer-events-none
          absolute
          -right-40
          bottom-10
          h-56
          w-56
          rounded-full
          bg-orange-500/[0.04]
          blur-[100px]

          min-[360px]:h-64
          min-[360px]:w-64

          sm:h-80
          sm:w-80
          sm:blur-[110px]
        "
      />

      {/* =====================================================
          SUBTLE GRID
      ====================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          inset-0
          opacity-[0.012]
          [background-image:linear-gradient(rgba(128,128,128,.5)_1px,transparent_1px),linear-gradient(90deg,rgba(128,128,128,.5)_1px,transparent_1px)]
          [background-size:70px_70px]
        "
      />

      {/* =====================================================
          MAIN CONTAINER
      ====================================================== */}

      <div
        className="
          relative
          mx-auto
          w-full
          max-w-7xl
          px-3
          min-[360px]:px-4
          sm:px-6
          lg:px-8
        "
      >
        {/* =====================================================
            HEADER
        ====================================================== */}

        <motion.div
          initial={{
            opacity: 0,
            y: 25,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
            amount: 0.2,
          }}
          transition={{
            duration: 0.6,
            ease: "easeOut",
          }}
          className="
            mx-auto
            mb-7
            w-full
            max-w-3xl
            text-center

            min-[360px]:mb-8

            sm:mb-10

            lg:mb-12
          "
        >
          {/* =================================================
              LABEL
          ================================================== */}

          <motion.span
            initial={{
              opacity: 0,
              scale: 0.9,
            }}
            whileInView={{
              opacity: 1,
              scale: 1,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.5,
            }}
            className="
              inline-flex
              max-w-full
              items-center
              justify-center
              rounded-full
              border
              border-orange-500/20
              bg-orange-500/[0.08]
              px-3
              py-1.5
              text-[9px]
              font-semibold
              uppercase
              tracking-[1.7px]
              text-[var(--brand-light)]

              min-[360px]:text-[10px]
              min-[360px]:tracking-[2px]

              sm:px-4
              sm:py-2
              sm:text-xs
            "
          >
            FAQs
          </motion.span>

          {/* =================================================
              HEADING
          ================================================== */}

          <h2
            className="
              mt-4
              break-words
              text-2xl
              font-bold
              leading-[1.15]
              tracking-tight
              text-[var(--text-primary)]

              min-[360px]:text-[26px]

              sm:mt-5
              sm:text-3xl

              md:text-4xl

              lg:text-5xl
            "
          >
            Software Development{" "}
            <span className="text-[var(--brand-primary)]">
              FAQs
            </span>
          </h2>

          {/* =================================================
              DESCRIPTION
          ================================================== */}

          <p
            className="
              mx-auto
              mt-3
              w-full
              max-w-5xl
              text-[11px]
              leading-5
              text-[var(--text-secondary)]

              min-[360px]:text-xs
              min-[360px]:leading-5

              sm:mt-4
              sm:text-sm
              sm:leading-6

              md:text-base
              md:leading-7
            "
          >
            Have questions about our software development services?
            Get quick answers about our development process, technology,
            project requirements, timelines, and how we help businesses
            build reliable and scalable software solutions.
          </p>
        </motion.div>

        {/* =====================================================
            FAQ GRID
        ====================================================== */}

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
            amount: 0.1,
          }}
          variants={containerVariants}
          className="
            mx-auto
            grid
            w-full
            max-w-6xl
            min-w-0
            grid-cols-1
            gap-2.5

            min-[360px]:gap-3

            sm:gap-4
          "
        >
          {faqs.map((faq, index) => (
            <FAQItem
              key={faq.question}
              faq={faq}
              index={index}
              isOpen={activeIndex === index}
              onToggle={() => handleToggle(index)}
            />
          ))}
        </motion.div>

        {/* =====================================================
            BOTTOM SUPPORT MESSAGE
        ====================================================== */}

        <motion.div
          initial={{
            opacity: 0,
            y: 15,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 0.5,
            delay: 0.2,
          }}
          className="
            mx-auto
            mt-6
            flex
            w-full
            max-w-xl
            flex-col
            items-center
            justify-center
            gap-2
            text-center

            min-[360px]:mt-7

            sm:mt-9
            sm:flex-row
            sm:flex-wrap
            sm:gap-3
          "
        >
          {/* =================================================
              STARS
          ================================================== */}

          <div
            className="
              flex
              shrink-0
              items-center
              gap-0.5
            "
          >
            {[1, 2, 3, 4, 5].map((star) => (
              <span
                key={star}
                className="
                  text-xs
                  text-[var(--brand-primary)]

                  sm:text-sm
                "
              >
                ★
              </span>
            ))}
          </div>

          {/* =================================================
              SUPPORT TEXT
          ================================================== */}

          <span
            className="
              max-w-full
              break-words
              text-[11px]
              leading-5
              text-[var(--text-muted)]

              min-[360px]:text-xs

              sm:text-sm
            "
          >
            Have more questions? Our team is here to help.
          </span>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;